import Link from 'next/link'
import { ArrowDownRight } from 'lucide-react'
import { cn } from '@/utilities/ui'

export type ExternalLinkButtonSize = 'md' | 'lg'
export type ExternalLinkButtonColor = 'brand' | 'light' | 'dark'

export interface ExternalLinkButtonProps {
  href: string
  children?: React.ReactNode
  text?: string
  external?: boolean
  size?: ExternalLinkButtonSize
  color?: ExternalLinkButtonColor
  className?: string
  'aria-label'?: string
  'aria-describedby'?: string
}

const sizeClasses = {
  md: 'text-base md:text-lg gap-6 md:gap-8 px-0 py-2.5 md:py-3',
  lg: 'text-lg md:text-xl gap-6 md:gap-8 px-0 py-3 md:py-4',
}

const colorClasses = {
  brand: 'text-brand',
  light: 'text-white',
  dark: 'text-text-primary',
}

// Underline is drawn with a pseudo element so we can fully control length and color
const baseClasses =
  'inline-flex items-center justify-start relative font-ui font-medium uppercase cursor-pointer before:absolute before:inset-0 before:border-b before:border-current before:pointer-events-none'

export default function ExternalLinkButton({
  href,
  children,
  text,
  external = false,
  size = 'md',
  color = 'brand',
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: ExternalLinkButtonProps) {
  const content = text || children

  const linkProps = {
    href,
    className: cn(baseClasses, sizeClasses[size], colorClasses[color], 'group', className),
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    ...(external && { target: '_blank', rel: 'noopener noreferrer' }),
  } as const

  return (
    <Link {...linkProps}>
      <span>{content}</span>
      <ArrowDownRight
        strokeWidth={2}
        className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-300 group-hover:-rotate-45"
      />
    </Link>
  )
}
